import React from "react";

/* ─── Card · obsidian chrome panel with optional neon outline ─── */
export const Card = ({ children, className = "", glow = "", ...rest }) => (
  <article
    className={`panel-chrome scanlines hud-corners ${glow ? `neon-${glow}` : ""} ${className}`}
    {...rest}
  >
    <span className="corner-b" />
    {children}
  </article>
);

/* ─── Overline · short caps label ─── */
export const Overline = ({ children, className = "", tone = "cyan" }) => {
  const colors = {
    cyan: "text-glow-cyan",
    magenta: "text-glow-magenta",
    violet: "text-glow-violet",
    gold: "text-glow-gold",
    green: "text-glow-green",
    muted: "text-[var(--text-3)]",
  };
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-[0.28em] ${colors[tone] || colors.cyan} ${className}`}
    >
      {children}
    </span>
  );
};

/* ─── Stat · massive numeric display ─── */
export const Stat = ({ label, value, sublabel, glow = "cyan", className = "", testId }) => {
  const glows = {
    cyan: "text-glow-cyan",
    magenta: "text-glow-magenta",
    violet: "text-glow-violet",
    gold: "text-glow-gold",
    green: "text-glow-green",
    red: "text-glow-red",
  };
  return (
    <div className={className} data-testid={testId}>
      <Overline tone={glow}>{label}</Overline>
      <div className={`font-display font-light text-5xl lg:text-6xl tracking-tight leading-none mt-4 ${glows[glow] || glows.cyan} breathe`}>
        {value}
      </div>
      {sublabel && (
        <div className="font-mono text-[10px] text-[var(--text-3)] mt-3 tracking-[0.2em] uppercase">
          {sublabel}
        </div>
      )}
    </div>
  );
};

/* ─── Tag · pill with neon outline ─── */
export const Tag = ({ children, tone = "default", className = "" }) => {
  const tones = {
    default:    "border-[var(--chrome-2)] text-[var(--text-2)]",
    fact:       "border-[var(--cyan)] text-[var(--cyan)]",
    conflict:   "border-[var(--red)] text-[var(--red)]",
    risk:       "border-[var(--amber)] text-[var(--amber)]",
    assumption: "border-[var(--gold)] text-[var(--gold)]",
    unknown:    "border-[var(--text-3)] text-[var(--text-3)]",
    cite:       "border-[var(--blue)] text-[var(--blue)]",
    pass:       "border-[var(--green)] text-[var(--green)] bg-[rgba(43,255,138,0.06)]",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-[3px] border text-[9px] font-mono uppercase tracking-[0.22em] rounded-none ${tones[tone] || tones.default} ${className}`}
    >
      {children}
    </span>
  );
};

/* ─── Buttons ─── */
export const BtnPrimary = ({ children, className = "", ...rest }) => (
  <button
    className={`relative inline-flex items-center gap-2 rounded-none font-display uppercase tracking-[0.2em] text-[11px] px-7 py-3 transition-all duration-300
      bg-gradient-to-b from-[#1a1d3a] to-[#0d0f24] text-[var(--cyan)] border border-[var(--cyan)]
      hover:from-[var(--cyan)] hover:to-[var(--cyan)] hover:text-[var(--void)]
      shadow-[0_0_20px_rgba(0,240,255,0.25)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)]
      disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const BtnSecondary = ({ children, className = "", ...rest }) => (
  <button
    className={`inline-flex items-center gap-2 rounded-none font-display uppercase tracking-[0.2em] text-[11px] px-7 py-3 transition-all duration-300
      bg-transparent text-[var(--text-2)] border border-[var(--chrome-2)]
      hover:text-[var(--magenta)] hover:border-[var(--magenta)] hover:shadow-[0_0_22px_rgba(255,43,214,0.35)]
      disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...rest}
  >
    {children}
  </button>
);

/* ─── SectionHeader ─── */
export const SectionHeader = ({ overline, title, description, action }) => (
  <header className="flex items-start justify-between gap-8 mb-12 relative">
    <div className="max-w-3xl">
      <Overline tone="magenta">{overline}</Overline>
      <h2 className="font-display text-4xl lg:text-5xl font-light tracking-[0.04em] mt-5 leading-none">
        <span className="text-glow-cyan">{title}</span>
      </h2>
      {description && (
        <p className="text-[var(--text-2)] mt-5 leading-relaxed max-w-2xl">
          {description}
        </p>
      )}
    </div>
    {action && <div className="flex items-center gap-3 flex-wrap justify-end">{action}</div>}
  </header>
);

/* ─── YAML block · pure-React highlighter, no dangerouslySetInnerHTML ─── */
const YAML_TOKEN_RE = /("[^"]*")|^(\s*)([A-Za-z0-9_\-#]+)(:)/gm;
export const YamlBlock = ({ value, className = "" }) => {
  const src = String(value || "");
  const parts = [];
  let lastIndex = 0;
  let m;
  let i = 0;
  YAML_TOKEN_RE.lastIndex = 0;
  while ((m = YAML_TOKEN_RE.exec(src)) !== null) {
    if (m.index > lastIndex) parts.push(src.slice(lastIndex, m.index));
    if (m[1]) {
      // quoted string
      parts.push(<span key={`s-${i++}`} className="s">{m[1]}</span>);
    } else {
      // key:
      parts.push(m[2]);
      parts.push(<span key={`k-${i++}`} className="k">{m[3]}</span>);
      parts.push(m[4]);
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < src.length) parts.push(src.slice(lastIndex));
  return <pre className={`yaml-block rounded-none ${className}`}>{parts}</pre>;
};

/* ─── EQ Bars · decorative live readout ─── */
export const EqBars = ({ count = 12, color = "cyan" }) => {
  const bars = Array.from({ length: count }, (_, i) => i);
  return (
    <div className="eq-bars" style={{ color: `var(--${color})` }}>
      {bars.map((i) => (
        <span
          key={`eq-${color}-${i}`}
          className="wave-bar"
          style={{
            background: `linear-gradient(0deg, var(--${color}), transparent)`,
            animationDelay: `${(i * 80) % 1000}ms`,
            height: `${30 + ((i * 13) % 70)}%`,
          }}
        />
      ))}
    </div>
  );
};

/* ─── Pulse dot · breathing status indicator ─── */
export const PulseDot = ({ color = "cyan", className = "" }) => (
  <span className={`relative inline-block w-2.5 h-2.5 ${className}`} style={{ color: `var(--${color})` }}>
    <span className="absolute inset-0 rounded-full" style={{ background: `var(--${color})`, boxShadow: `0 0 12px var(--${color})` }} />
    <span className="absolute inset-0 rounded-full pulse-ring" />
  </span>
);

/* ─── Input · field used across the app ─── */
export const NeonInput = React.forwardRef(({ className = "", ...rest }, ref) => (
  <input
    ref={ref}
    className={`w-full bg-[var(--void-2)] border border-[var(--chrome)] rounded-none p-3 font-mono text-sm text-[var(--text)] placeholder:text-[var(--text-3)]
      focus:outline-none focus:border-[var(--cyan)] focus:shadow-[0_0_18px_rgba(0,240,255,0.35)] transition-all ${className}`}
    {...rest}
  />
));
NeonInput.displayName = "NeonInput";

export const NeonSelect = ({ className = "", children, ...rest }) => (
  <select
    className={`w-full bg-[var(--void-2)] border border-[var(--chrome)] rounded-none p-3 font-mono text-xs uppercase tracking-[0.18em] text-[var(--text)]
      focus:outline-none focus:border-[var(--cyan)] focus:shadow-[0_0_18px_rgba(0,240,255,0.35)] transition-all ${className}`}
    {...rest}
  >
    {children}
  </select>
);

export const NeonTextarea = React.forwardRef(({ className = "", ...rest }, ref) => (
  <textarea
    ref={ref}
    className={`w-full bg-[var(--void-2)] border border-[var(--chrome)] rounded-none p-4 font-mono text-sm leading-relaxed text-[var(--text)] placeholder:text-[var(--text-3)]
      focus:outline-none focus:border-[var(--cyan)] focus:shadow-[0_0_18px_rgba(0,240,255,0.35)] transition-all resize-none ${className}`}
    {...rest}
  />
));
NeonTextarea.displayName = "NeonTextarea";
